/**
 * 业务配置项文件，主要区分不同地区业务添加的配置项
 * 根据初次加载的页面路由匹配 window.location.href
 *
 * 添加了配置项，在globals.d.ts加下，可以有代码提示
 *
 * 不要直接在模版代码中使用，因为会被打包到上下文中，这个配置只在运行时有用
 */
import { merge } from 'lodash-es'
import useAppStore from '@shared/store/useAppStore'

/** 基础配置 */
const BASE_CONFIG = {
  /** 显示在页面的系统名称 */
  systemName: '智慧排水平台',
  mapboxToken: 'pk.eyJ1IjoiZW5yaXF1ZWdydWJla2luZyIsImEiOiJjbGkxNGt4dGUwNWZqM2xtdWVlbTE2ZmRzIn0.crw0-80CE_ufD_cUMGN56w',
  geoserverMaps: 'https://maps.dev.zrshuiwu.com',
  tdtSearch: '南昌',
  gisManageTemplate: 'https://file.dev.zrshuiwu.com/group1/default/system/template/管网导入模板_360113.zip',
  privateKey1: '4a32179fcabc9423ee39d2dca734ae23814d4890140e05e764fea89dd331d239', //前端私钥
  tdtMap: '/NCMAP/NCTDTYX/{z}/{x}/{y}.jpg',
  publicKey2:
    '04d65f6df4d118708515fb7be0fe8de57c36ed667bbbb974a745f655b8a3065add69cd48bf884a8eb44e0352ba45519f4be7c8ac70b558e5baf60930abeeb5c6f1', // 后端公钥
  whiteModel:
    'https://gis.dev.zrshuiwu.com/geoserver/gwc/service/tms/1.0.0/long%3Ahgtbuid@EPSG%3A900913@pbf/{z}/{x}/{y}.pbf',
  whiteModelLayer: 'hgtbuid'
}

/** 根据路由添加配置项， 以下配置项通过 merge函数 深层次继承合并 BASE_CONFIG */

const configMap = {
  /**-------------------- 红谷滩开发 --------------------*/
  'zrsite.dev.zrshuiwu.com': {},

  /**-------------------- 西湖开发 --------------------*/
  'xihu.dev.zrshuiwu.com': {
    systemName: '西湖区排口智能监测系统',
    gisManageTemplate: 'https://file.dev.zrshuiwu.com/group1/default/system/template/管网导入模板_360113.zip'
  },

  /**-------------------- 西湖生产 --------------------*/
  'xihusthb.zhihuicenter.cn': {
    systemName: '西湖区排口智能监测系统',
    geoserverMaps: 'https://xihusthb.zhihuicenter.cn',
    gisManageTemplate: 'https://file.dev.zrshuiwu.com/group1/default/system/template/管网导入模板_360103.zip'
  },

  /**-------------------- 高新开发 --------------------*/
  'gaoxin.dev.zrshuiwu.com': {
    systemName: '高新区水环境监测平台',
    gisManageTemplate: 'https://file.dev.zrshuiwu.com/group1/default/system/template/管网导入模板_330096.zip'
  },
  'zrpre.dev.zrshuiwu.com': {
    systemName: '智慧排水平台',
    tdtSearch: '南昌',
    gisManageTemplate: 'https://file.dev.zrshuiwu.com/group1/default/system/template/管网导入模板_360000.zip'
  },

  /**-------------------- 赣州开发 --------------------*/
  'ganzhou.dev.zrshuiwu.com': {
    systemName: '赣州市排口智能监测系统',
    systemKey: 'GANZHOU',
    tdtSearch: '赣州'
  },
  /**-------------------- 高新本地生产 --------------------*/
  '192.168.110.128': {
    systemName: '高新区水环境监测平台',
    geoserverMaps: 'http://192.168.110.128',
    gisManageTemplate: 'http://192.168.110.128:8081/group1/default/system/template/管网导入模板_330096.zip'
  },
  /**-------------------- 修水 --------------------*/
  'xiushui.demo.zrshuiwu.com': {
    systemName: '修水智慧排水平台',
    tdtSearch: '修水',
    hiddenAI: true,
    gisManageTemplate: 'https://file.dev.zrshuiwu.com/group1/default/system/template/管网导入模板_360424.zip'
  },
  /**-------------------- 修水本地生产配置 --------------------*/
  '192.168.12.220': {
    systemName: '修水智慧排水平台',
    tdtSearch: '修水',
    hiddenAI: true,
    geoserverMaps: 'http://192.168.12.220',
    gisManageTemplate: 'http://192.168.12.220:8081/group1/default/system/template/管网导入模板_360424.zip'
  },
  /**-------------------- 上饶--------------------*/
  'shangrao.demo.zrshuiwu.com': {
    systemName: '上饶智慧排水平台',
    tdtSearch: '上饶'
  }
}

const getConfig = () => {
  const appStore = useAppStore()
  if (appStore.projectConfig.ADMIN_BASE_CONFIG) {
    // 如果后台配置了基础配置，直接使用后台配置
    console.log('-------走后台配置-------')
    let config = {}
    if (import.meta.env.MODE === 'development') {
      // 开发模式使用的配置
      config = {
        ...BASE_CONFIG,
        ...appStore.projectConfig.ADMIN_BASE_CONFIG,
        ...appStore.projectConfig.ADMIN_DEV_CONFIG
      }
    } else {
      // 开发模式使用的配置
      config = {
        ...BASE_CONFIG,
        ...appStore.projectConfig.ADMIN_BASE_CONFIG,
        ...appStore.projectConfig.ADMIN_PROD_CONFIG
      }
    }
    return config
  } else {
    // 原有逻辑
    console.log('-------走本地配置-------')
    let config = BASE_CONFIG
    if (import.meta.env.MODE === 'development') {
      // 开发模式使用的配置
      config = {
        ...BASE_CONFIG,
        ...configMap['zrsite.dev.zrshuiwu.com']
      }
    }

    // 根据url匹配配置
    Object.keys(configMap).forEach((key) => {
      if (window.location.href.includes(key))
        config = {
          ...BASE_CONFIG,
          ...configMap[key]
        }
    })

    return config
  }
}
export const initConfig = () => {
  const config = getConfig()
  document.title = config.systemName

  window.__SYS_CONFIG = config
}
